import { useState } from "react";
import { Input } from "@/components/ui/input";
import type { FieldConfig } from "../types";

interface NumberFieldProps<T> {
  field: FieldConfig<T>;
  value: number | undefined;
  onChange: (value: number | undefined) => void;
  onBlur: () => void;
  disabled?: boolean;
  id: string;
  placeholder?: string;
  "aria-describedby"?: string;
  "aria-required"?: boolean;
}

// ── Plain number input (default) ─────────────────────────────────────────────
// Uses native type="number" — fine for non-negative counts, ages, etc.
// A lone "-" is NOT a valid number so a controlled type="number" can't hold
// intermediate negative states; that's handled by SignedNumberInput instead.
function PlainNumberInput<T>({
  value,
  onChange,
  onBlur,
  disabled,
  id,
  placeholder,
  "aria-describedby": ariaDescribedBy,
  "aria-required": ariaRequired,
}: Omit<NumberFieldProps<T>, "field">) {
  return (
    <Input
      id={id}
      type="number"
      placeholder={placeholder}
      disabled={disabled}
      aria-describedby={ariaDescribedBy}
      aria-required={ariaRequired}
      value={value ?? ""}
      onChange={(e) => {
        const v = e.target.value.trim();
        if (v === "") {
          onChange(undefined);
        } else {
          const parsed = parseFloat(v);
          onChange(isNaN(parsed) ? undefined : parsed);
        }
      }}
      onBlur={onBlur}
      inputMode="decimal"
    />
  );
}

// ── Signed number input (opt-in via allowNegative) ───────────────────────────
// Uses type="text" + inputMode="decimal" so the local string buffer can hold
// intermediate states such as "-", "-.", "1." without being wiped by React's
// controlled-input reconciliation.
//
// State reseed pattern (React "adjust state during render", no useEffect):
//   When an external value change disagrees with what the buffer holds we
//   reseed the buffer. This handles edit-dialog hydration and form resets.
const SIGNED_DECIMAL_RE = /^-?\d*\.?\d*$/;

const fmt = (n: number | undefined): string =>
  n === undefined || n === null ? "" : String(n);

const parse = (s: string): number | undefined => {
  const t = s.trim();
  if (t === "" || t === "-" || t === "." || t === "-.") return undefined;
  const n = parseFloat(t);
  return Number.isNaN(n) ? undefined : n;
};

function SignedNumberInput<T>({
  value,
  onChange,
  onBlur,
  disabled,
  id,
  placeholder,
  "aria-describedby": ariaDescribedBy,
  "aria-required": ariaRequired,
}: Omit<NumberFieldProps<T>, "field">) {
  const [text, setText] = useState<string>(() => fmt(value));
  const [prevValue, setPrevValue] = useState(value);

  // Adjust state during render: if the external value changed AND the buffer
  // disagrees with it, reseed the buffer (handles reset / edit hydration).
  if (value !== prevValue) {
    setPrevValue(value);
    if (parse(text) !== value) {
      setText(fmt(value));
    }
  }

  return (
    <Input
      id={id}
      type="text"
      inputMode="decimal"
      placeholder={placeholder}
      disabled={disabled}
      aria-describedby={ariaDescribedBy}
      aria-required={ariaRequired}
      value={text}
      onChange={(e) => {
        const raw = e.target.value;
        // Reject keystrokes that don't form a valid signed-decimal prefix.
        if (raw !== "" && !SIGNED_DECIMAL_RE.test(raw)) return;
        setText(raw);
        onChange(parse(raw));
      }}
      onBlur={onBlur}
    />
  );
}

// ── Dispatcher ────────────────────────────────────────────────────────────────
export function NumberField<T>({
  field,
  value,
  onChange,
  onBlur,
  disabled,
  id,
  placeholder,
  "aria-describedby": ariaDescribedBy,
}: NumberFieldProps<T>) {
  if (field.allowNegative) {
    return (
      <SignedNumberInput<T>
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        disabled={disabled}
        id={id}
        placeholder={placeholder}
        aria-describedby={ariaDescribedBy}
        aria-required={field.required || undefined}
      />
    );
  }
  return (
    <PlainNumberInput<T>
      value={value}
      onChange={onChange}
      onBlur={onBlur}
      disabled={disabled}
      id={id}
      placeholder={placeholder}
      aria-describedby={ariaDescribedBy}
      aria-required={field.required || undefined}
    />
  );
}
